import {
  addDoc,
  collection,
  disableNetwork,
  doc,
  enableNetwork,
  onSnapshot,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import firebaseFirestore from '~/config/firebase/firestore';
import { Location } from '~/core/types';
import { franjasDeBloque } from '~/pages/public/Forms/Reservation/repositories/franjas';
import { getHoursInRange, homologateDay } from '~/utils/date';

/**
 * Deja constancia de un fallo del calendario en `errorLogs`.
 *
 * Hasta ahora los fallos de carga se tragaban: sin `catch`, sin aviso y con
 * Sentry apagado en producción. Cuando el calendario salía vacío no había ni
 * un solo rastro que mirar. Esto es lo que faltaba para poder investigar el
 * "a veces no carga nada" con datos y no con suposiciones.
 */
const anotarFallo = (donde: string, error: unknown, extra: Record<string, unknown> = {}) => {
  const e = error as { code?: string; message?: string; stack?: string };
  console.error(`[calendario] ${donde}`, error);
  addDoc(collection(firebaseFirestore, 'errorLogs'), {
    origen: 'calendario',
    donde,
    code: e?.code ?? null,
    message: `[calendario] ${donde}: ${e?.message ?? String(error)}`,
    stack: e?.stack ?? null,
    ...extra,
    url: typeof window !== 'undefined' ? window.location.href : null,
    timestamp: new Date(),
  }).catch(() => undefined);
};

/**
 * Forzar que Firestore vuelva a conectarse ahora mismo.
 *
 * Cuando el SDK se da por "sin conexión" (le basta UN intento fallido del
 * canal o 10 s sin lograrlo, medido en su código: `MAX_WATCH_STREAM_FAILURES
 * = 1`, `ONLINE_STATE_TIMEOUT_MS = 10 s`), reintenta solo, pero con esperas
 * que llegan al minuto. Apagar y encender la red es la vía oficial para que
 * lo intente ya; las escuchas y escrituras pendientes se conservan.
 */
let reconectando: Promise<void> | null = null;
export const reconectarFirestore = (): Promise<void> => {
  if (!reconectando) {
    reconectando = disableNetwork(firebaseFirestore)
      .then(() => enableNetwork(firebaseFirestore))
      .catch((e) => anotarFallo('reconectar', e))
      .finally(() => {
        reconectando = null;
      });
  }
  return reconectando;
};

/**
 * Datos para la vista de calendario.
 *
 * Dos decisiones que evitan los fallos típicos de estas vistas:
 *
 *  · **Las horas se leen de los campos de texto de la reserva** (`startHour`,
 *    `endHour`), no de las marcas de tiempo. Esas marcas están en UTC y
 *    convertirlas desplaza las reservas media jornada. El panel ya muestra
 *    estos mismos textos, así que el calendario coincide con la tabla.
 *
 *  · **Todo lo pesado se calcula una sola vez** al llegar los datos, no en cada
 *    pintado: en tiempo real llegan avisos constantes y recalcular la rejilla
 *    entera en cada uno hace la vista lenta.
 */

export const MINUTOS_POR_FILA = 30;

export type EstadoReserva = 'pending' | 'confirmed' | 'checkin' | 'checkout' | 'cancelled';

export type BloqueAgenda = {
  id: string;
  code: string;
  cliente: string;
  locationId: string;
  filaInicio: number;
  filas: number;
  horaInicio: string;
  horaFin: string;
  personas: number;
  estado: EstadoReserva;
  /** Comparte franja con otra reserva de la misma sala: hay que avisarlo. */
  enConflicto?: boolean;
  /** Posición cuando dos se solapan: 0 = izquierda, 1 = derecha… */
  carril: number;
  carriles: number;
  /** La reserva tal cual llega: la necesitan los botones de cambio de estado. */
  reserva: ReservaCruda;
};

export type EspacioAgenda = {
  id: string;
  nombre: string;
  /** Zonas de día completo: no se pintan por franjas, sino como lista. */
  diaCompleto: boolean;
  cupo: number;
  porPersona: boolean;
  apertura: string;
  cierre: string;
  /** Cuánta gente cabe; sale de `schedule.settings.maximumCapacity`. */
  capacidad: number;
  /** Si la reserva pide hora de fin (`isEndDateEnable`). */
  conHoraFin: boolean;
  /**
   * Minutos que dura una reserva sin hora de fin (`blockTimeMinutes`; la
   * visita "Conocer Coworking" dura 60). Sin esto se pintaba de media hora.
   */
  bloqueo: number;
  /** Horario por día de la semana. **Las claves van con 0 = lunes.** */
  semana: Record<string, { opening?: string; closing?: string; isOpen?: boolean }>;
  /**
   * El mapa de ocupación de la sede (`locations/{id}.reservations`), tal cual:
   * `{ 'yyyy-MM-dd': { 'HH:mm': [ids] } }`. Es lo que mira el formulario para
   * decidir qué horas ofrece, así que es lo que mira el calendario también.
   */
  ocupacion: Record<string, Record<string, string[]>>;
};

/**
 * La configuración de las visitas ("Conocer Coworking"): sin hora de fin y
 * con bloque de tiempo. Solo a estos espacios se les aplica lo de abajo.
 */
export const esVisita = (e: EspacioAgenda): boolean =>
  !e.conHoraFin && !e.diaCompleto && e.bloqueo > 0;

/** Horario del espacio ese día. */
export const horarioDelDia = (e: EspacioAgenda, fecha: Date) => {
  const propio = e.semana?.[homologateDay[fecha.getDay()]];
  return {
    apertura: propio?.opening ?? e.apertura,
    cierre: propio?.closing ?? e.cierre,
    abierto: propio?.isOpen !== false,
  };
};

/**
 * Filas de la rejilla en las que NO puede empezar una visita ese día.
 *
 * Es la misma regla que el desplegable del formulario (`getStartHoursAvailable`):
 * una visita a las H ocupa las franjas de `franjasDeBloque` (el bloque más
 * media hora de margen antes), así que solo puede empezar donde todas ellas
 * tengan sitio en el mapa de ocupación de la sede; y no se ofrece empezar tan
 * tarde que el bloque no quepa antes del cierre. Antes el calendario dejaba
 * arrastrar cualquier hora libre a la vista, y el formulario la rechazaba.
 */
export const filasSinSitioParaVisita = (
  e: EspacioAgenda,
  fecha: Date,
  rejilla: { inicio: number; filas: number; horas: string[] }
): Set<number> => {
  const sinSitio = new Set<number>();
  const { apertura, cierre, abierto } = horarioDelDia(e, fecha);
  const horasDelDia = abierto ? getHoursInRange(apertura, cierre) : [];
  // Como en el formulario: las últimas `bloqueo / 30` no se ofrecen.
  const ofrecidas = new Set(
    horasDelDia.slice(0, Math.max(horasDelDia.length - e.bloqueo / MINUTOS_POR_FILA, 0))
  );
  const marcas = e.ocupacion?.[format(fecha, 'yyyy-MM-dd')] ?? {};
  const cupo = e.cupo > 0 ? e.cupo : 1;

  rejilla.horas.forEach((hora, fila) => {
    if (!ofrecidas.has(hora)) {
      sinSitio.add(fila);
      return;
    }
    const cabe = franjasDeBloque({
      startHour: hora,
      blockTimeMinutes: e.bloqueo,
      primeraHora: horasDelDia[0],
      ultimaHora: horasDelDia[horasDelDia.length - 1],
    }).every((franja) => (marcas[franja]?.length ?? 0) + 1 <= cupo);
    if (!cabe) sinSitio.add(fila);
  });
  return sinSitio;
};

/**
 * Orden de las columnas, tal como NOI lee su día:
 * primero las salas, después las creativas, luego la zona de coworking y al
 * final las visitas para conocer el espacio, que no son una reserva de sala.
 * Dentro de cada grupo, de menor a mayor capacidad.
 */
const grupoDe = (nombre: string, diaCompleto: boolean): number => {
  const n = nombre.toLowerCase();
  if (n.includes('conocer')) return 4;
  if (diaCompleto || n.includes('coworking')) return 3;
  if (n.includes('creativa')) return 2;
  return 1;
};

const aMinutos = (hora: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec((hora ?? '').trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/** '08:00 AM' → '08:00'. Las reservas antiguas guardan la hora así. */
const normalizarHora = (valor?: string | null): string | null => {
  if (!valor) return null;
  const t = valor.trim();
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2];
  const periodo = m[3]?.toUpperCase();
  if (periodo === 'PM' && h < 12) h += 12;
  if (periodo === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
};


/** Espacios de la empresa, ya ordenados como se explica en `grupoDe`. */
export const useEspacios = (companyId?: string) => {
  const [espacios, setEspacios] = useState<EspacioAgenda[]>([]);
  const [cargando, setCargando] = useState(true);
  const [desdeCache, setDesdeCache] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cambiarlo vuelve a suscribirse: es el "Reintentar".
  const [intento, setIntento] = useState(0);
  const reintentar = () => {
    setIntento((n) => n + 1);
    reconectarFirestore();
  };

  useEffect(() => {
    if (!companyId) return;
    setCargando(true);
    setError(null);
    setDesdeCache(false);

    const q = query(
      collection(firebaseFirestore, 'locations'),
      where('company', '==', doc(firebaseFirestore, 'companies', companyId)),
      where('status', '==', 'active')
    );

    // Antes era un `getDocs` de una sola vez, y ahí estaba una de las causas
    // del "a veces no carga nada": si el SDK se creía sin conexión en ese
    // instante (portátil recién despertado, wifi renegociando), `getDocs`
    // resuelve AL MOMENTO con la caché en memoria —vacía— sin ningún error,
    // y el calendario se quedaba sin columnas hasta cerrarlo y abrirlo. Con
    // una escucha viva, la respuesta del servidor llega en cuanto vuelve la
    // conexión, y `fromCache` dice si lo que hay es de verdad o provisional.
    const parar = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        const lista = snap.docs.map((d) => {
          const datos = d.data() as Location & { schedule?: any };
          const ajustes = datos.schedule?.settings ?? {};
          const semana = datos.schedule?.['weekly-dates'] ?? {};
          const cualquiera = Object.values(semana)[0] as { opening?: string; closing?: string };
          return {
            id: d.id,
            nombre: datos.name ?? d.id,
            diaCompleto: Boolean(ajustes.isReservationWholeDay),
            cupo: Number(ajustes.numberBookingsAllow ?? 1),
            porPersona: Boolean(ajustes.personHasSpecificPosition),
            apertura: cualquiera?.opening ?? '06:00',
            cierre: cualquiera?.closing ?? '23:00',
            capacidad: Number(ajustes.maximumCapacity ?? 0),
            conHoraFin: Boolean(ajustes.isEndDateEnable),
            bloqueo: Number(ajustes.blockTimeMinutes ?? 0),
            semana,
            ocupacion: (datos as Location & { reservations?: any }).reservations ?? {},
          };
        });
        lista.sort((a, b) => {
          const ga = grupoDe(a.nombre, a.diaCompleto);
          const gb = grupoDe(b.nombre, b.diaCompleto);
          if (ga !== gb) return ga - gb;
          if (a.capacidad !== b.capacidad) return a.capacidad - b.capacidad;
          return a.nombre.localeCompare(b.nombre, 'es');
        });
        setEspacios(lista);
        setDesdeCache(snap.metadata.fromCache);
        setError(null);
        // Una respuesta de caché vacía no es "no hay salas": es "aún no se
        // sabe". Se sigue esperando (con aviso) en vez de pintar el vacío.
        if (!snap.metadata.fromCache || lista.length > 0) setCargando(false);
      },
      (e) => {
        // Antes esto no se atrapaba: el calendario quedaba sin columnas, sin
        // mensaje y sin rastro, hasta que alguien cerraba y volvía a abrir.
        anotarFallo('cargar las salas', e, { companyId, intento });
        setError('No se pudieron cargar las salas.');
        setCargando(false);
      }
    );
    return () => parar();
  }, [companyId, intento]);

  return { espacios, cargando, desdeCache, error, reintentar };
};

export type ReservaCruda = {
  id: string;
  code?: string;
  namesAndSurnames?: string;
  name?: string;
  email?: string;
  companyName?: string;
  phoneNumber?: string;
  phone?: string;
  startHour?: string;
  endHour?: string;
  time?: string;
  numberPeople?: number;
  status?: string;
  statusHistory?: unknown[];
  payment?: { amount?: number | string };
  datetime?: Timestamp;
  location?: { id?: string; name?: string; company?: unknown };
};

/**
 * Reservas de un rango de fechas, en tiempo real.
 *
 * Usa el mismo filtro que la tabla de reservas (empresa + rango de `datetime`),
 * que ya tiene su índice creado.
 */
export const useReservasEnRango = (companyId: string | undefined, desde: Date, hasta: Date) => {
  const [reservas, setReservas] = useState<ReservaCruda[]>([]);
  const [cargando, setCargando] = useState(true);
  // Lo último que llegó vino de la caché en memoria, no del servidor: el SDK
  // está (o se cree) sin conexión y la lista puede estar incompleta.
  const [desdeCache, setDesdeCache] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  const reintentar = () => {
    setIntento((n) => n + 1);
    reconectarFirestore();
  };
  const cancelar = useRef<null | (() => void)>(null);

  const desdeMs = desde.getTime();
  const hastaMs = hasta.getTime();

  useEffect(() => {
    if (!companyId) return;
    setCargando(true);
    setError(null);
    setDesdeCache(false);
    cancelar.current?.();

    const q = query(
      collection(firebaseFirestore, 'reservations'),
      where('location.company', '==', doc(firebaseFirestore, 'companies', companyId)),
      where('datetime', '>=', Timestamp.fromMillis(desdeMs)),
      where('datetime', '<=', Timestamp.fromMillis(hastaMs))
    );

    // `includeMetadataChanges` es lo que permite saber si lo que llega es del
    // servidor o de la caché. Sin conexión, Firestore entrega en el acto una
    // lista vacía "de caché" y sin error: pintarla como si fuera verdad es
    // exactamente el "no carga ninguna reserva" que se veía de vez en cuando.
    const parar = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        setReservas(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ReservaCruda)));
        setDesdeCache(snap.metadata.fromCache);
        setError(null);
        if (!snap.metadata.fromCache || !snap.empty) setCargando(false);
      },
      (e) => {
        // Un error aquí MATA la suscripción: Firestore no la reanuda sola. Si
        // se ignora, la vista se queda vacía y muda. Se avisa y se ofrece
        // volver a suscribirse.
        anotarFallo('escuchar las reservas', e, {
          companyId,
          desde: new Date(desdeMs).toISOString(),
          hasta: new Date(hastaMs).toISOString(),
          intento,
        });
        setError('No se pudieron cargar las reservas.');
        setCargando(false);
      }
    );
    cancelar.current = parar;
    return () => parar();
  }, [companyId, desdeMs, hastaMs, intento]);

  return { reservas, cargando, desdeCache, error, reintentar };
};

type ArmarParams = {
  reservas: ReservaCruda[];
  espacios: EspacioAgenda[];
  inicioRejilla: number;
  totalFilas: number;
  minutosBloqueoPorEspacio?: Record<string, number>;
};

/**
 * Convierte las reservas en bloques colocables en la rejilla.
 *
 * Reparte en carriles las que comparten franja dentro de una misma sala: en vez
 * de taparse una a otra —que es como se ocultan los problemas— se ven las dos,
 * lado a lado y marcadas.
 */
export const armarBloques = ({
  reservas,
  espacios,
  inicioRejilla,
  totalFilas,
  minutosBloqueoPorEspacio = {},
}: ArmarParams): Record<string, BloqueAgenda[]> => {
  const porEspacio: Record<string, BloqueAgenda[]> = {};
  espacios.forEach((e) => (porEspacio[e.id] = []));

  for (const r of reservas) {
    const locationId = r.location?.id ?? '';
    const espacio = espacios.find((e) => e.id === locationId);
    if (!espacio || espacio.diaCompleto) continue;

    const inicio = aMinutos(normalizarHora(r.startHour) ?? normalizarHora(r.time) ?? '');
    if (inicio === null) continue;

    const finTexto = normalizarHora(r.endHour);
    const bloqueo = minutosBloqueoPorEspacio[locationId] ?? 0;
    const fin =
      finTexto !== null
        ? aMinutos(finTexto)
        : bloqueo > 0
        ? inicio + bloqueo
        : inicio + MINUTOS_POR_FILA;

    const filaInicio = Math.round((inicio - inicioRejilla) / MINUTOS_POR_FILA);
    const filas = Math.max(1, Math.round(((fin ?? inicio) - inicio) / MINUTOS_POR_FILA));
    if (filaInicio < 0 || filaInicio >= totalFilas) continue;

    porEspacio[locationId].push({
      id: r.id,
      code: (r.code ?? '').toUpperCase(),
      cliente: r.namesAndSurnames ?? r.name ?? 'Sin nombre',
      locationId,
      filaInicio,
      filas: Math.min(filas, totalFilas - filaInicio),
      horaInicio: normalizarHora(r.startHour) ?? normalizarHora(r.time) ?? '',
      horaFin: finTexto ?? '',
      personas: Number(r.numberPeople ?? 1),
      estado: (r.status ?? 'pending') as EstadoReserva,
      carril: 0,
      carriles: 1,
      reserva: r,
    });
  }

  // Carriles: dos reservas que se pisan se muestran una al lado de la otra.
  for (const [id, bloques] of Object.entries(porEspacio)) {
    const activos = bloques
      .filter((b) => b.estado !== 'cancelled')
      .sort((a, b) => a.filaInicio - b.filaInicio || b.filas - a.filas);
    const cancelados = bloques.filter((b) => b.estado === 'cancelled');

    const grupos: BloqueAgenda[][] = [];
    for (const b of activos) {
      const grupo = grupos.find((g) =>
        g.some((o) => b.filaInicio < o.filaInicio + o.filas && o.filaInicio < b.filaInicio + b.filas)
      );
      if (grupo) grupo.push(b);
      else grupos.push([b]);
    }
    grupos.forEach((g) => {
      g.forEach((b, i) => {
        b.carril = i;
        b.carriles = g.length;
        b.enConflicto = g.length > 1;
      });
    });
    porEspacio[id] = [...activos, ...cancelados];
  }

  return porEspacio;
};

/** Reservas de una zona de día completo, con el total de plazas ocupadas. */
export const ocupacionDiaCompleto = (reservas: ReservaCruda[], espacioId: string) => {
  const dentro = reservas.filter(
    (r) => r.location?.id === espacioId && r.status !== 'cancelled'
  );
  const plazas = dentro.reduce((n, r) => n + Math.max(Number(r.numberPeople ?? 1), 1), 0);
  return {
    plazas,
    personas: dentro
      .map((r) => ({
        id: r.id,
        code: (r.code ?? '').toUpperCase(),
        cliente: r.namesAndSurnames ?? r.name ?? 'Sin nombre',
        personas: Number(r.numberPeople ?? 1),
        estado: (r.status ?? 'pending') as EstadoReserva,
        hora: normalizarHora(r.startHour) ?? normalizarHora(r.time) ?? '',
        reserva: r,
      }))
      .sort((a, b) => a.cliente.localeCompare(b.cliente, 'es')),
  };
};

/** Franja horaria que abarca todos los espacios que se muestran. */
export const useRejilla = (espacios: EspacioAgenda[], fecha: Date) => {
  return useMemo(() => {
    const porHoras = espacios.filter((e) => !e.diaCompleto);
    if (porHoras.length === 0) return { inicio: 6 * 60, fin: 23 * 60, filas: 34, horas: [] };

    const aperturas: number[] = [];
    const cierres: number[] = [];
    porHoras.forEach((e) => {
      // Antes se buscaba con el nombre del día ('monday'), pero las claves
      // son numéricas con 0 = lunes: nunca acertaba y usaba el horario del
      // lunes para todos los días.
      const { apertura, cierre } = horarioDelDia(e, fecha);
      aperturas.push(aMinutos(apertura) ?? 6 * 60);
      cierres.push(aMinutos(cierre) ?? 23 * 60);
    });
    const inicio = Math.min(...aperturas);
    const fin = Math.max(...cierres);
    const filas = Math.max(1, Math.round((fin - inicio) / MINUTOS_POR_FILA));
    const horas = Array.from({ length: filas }, (_, i) => {
      const m = inicio + i * MINUTOS_POR_FILA;
      return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    });
    return { inicio, fin, filas, horas };
  }, [espacios, fecha]);
};

/** Minuto actual, actualizado al filo de cada minuto y no antes. */
export const useMinutoActual = (activo: boolean) => {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    if (!activo) return;
    let intervalo: ReturnType<typeof setInterval>;
    // Se sincroniza con el reloj: primero espera al minuto redondo.
    const espera = setTimeout(() => {
      setAhora(new Date());
      intervalo = setInterval(() => setAhora(new Date()), 60000);
    }, (60 - new Date().getSeconds()) * 1000);
    return () => {
      clearTimeout(espera);
      if (intervalo) clearInterval(intervalo);
    };
  }, [activo]);
  return ahora;
};
